/**
 * Ballistic arcs, rockets in the air, interceptor chase lines — as a MapLibre custom WebGL layer.
 *
 * Why not deck.gl (tried 2026-09-02): deck's globe camera has no pitch or bearing, so on MapLibre's
 * tilted globe every arc landed in the wrong place ("rockets in orbit", a halo round the limb).
 * MapLibre hands custom layers its own projection prelude — `projectTileFor3D(mercatorXY, elevation)`
 * — the very function its own layers use, so this lines up on the globe, tilted, and through the
 * globe→mercator transition, for free. Under the globe variant elevation is metres; under mercator
 * it is mercator z, converted in the shader.
 *
 * Geometry: per missile the arc launch→target is sampled along the great circle and lifted by a sine
 * of progress (peak = min(10% of range, ~250 px at the zoom)). Positions live in a static VBO rebuilt
 * only when the missile set or the zoom step changes; per frame only a one-float-per-vertex
 * `progress` VBO is uploaded and the colour split (bright behind the rocket / faint ahead) happens in
 * the vertex shader. Lines are screen-space quads extruded in clip space, so width is in pixels.
 */

import maplibregl, { type Map as MLMap, type CustomLayerInterface } from "maplibre-gl";
import { world, missileType } from "./store.svelte";
import { distanceKm, move, missileFlight, type MapFilters, type MapSettings, type Pos } from "./map";

const rad = (d: number) => (d * Math.PI) / 180;
function bearing(a: Pos, b: Pos) {
  const la1 = rad(a.lat), la2 = rad(b.lat), dLon = rad(b.lon - a.lon);
  return Math.atan2(Math.sin(dLon) * Math.cos(la2), Math.cos(la1) * Math.sin(la2) - Math.sin(la1) * Math.cos(la2) * Math.cos(dLon));
}
const MAX_LIFT_PX = 250;
function peakM(totalKm: number, zoom: number, lat: number) {
  const mPerPx = (156_543 * Math.cos(rad(lat))) / Math.pow(2, zoom);
  return Math.max(300, Math.min(totalKm * 100, MAX_LIFT_PX * mPerPx));
}
const merc = (p: Pos) => { const lon = ((p.lon + 540) % 360) - 180; const m = maplibregl.MercatorCoordinate.fromLngLat([lon, p.lat]); return [m.x, m.y] as [number, number]; };

/** `?arcs=off` (sticks in localStorage) disables the layer entirely — the A/B control. */
export function arcsEnabled(): boolean {
  try {
    const q = new URLSearchParams(location.search).get("arcs");
    if (q === "off" || q === "on") localStorage.setItem("launch:arcs", q);
    return localStorage.getItem("launch:arcs") !== "off";
  } catch { return true; }
}

// ---- shaders --------------------------------------------------------------------------------------

const COMMON = `
uniform vec2 u_viewport;
// elevation → what projectTileFor3D wants: metres on the globe, mercator z under mercator
float mercZ(float metres, float y) { float lat = atan(sinh(3.14159265 * (1.0 - 2.0 * y))); return metres / (40075016.686 * cos(lat)); }
vec4 proj(vec2 p, float e) {
  #ifdef GLOBE
  // projectTileFor3D keeps the true depth (it expects a depth test); we draw without one, so hide the far
  // side the way MapLibre's 2-D path does: replace z with the globe clipping term of the ELEVATED point
  vec3 sp = projectToSphere(p);
  vec4 r = interpolateProjectionFor3D(p, sp, e);
  // clip by the GROUND point, not the elevated one: a rocket whose track is behind the planet stays hidden
  // instead of peeking over the limb like a satellite (Cam, 2026-09-03: "rendering behind the earth")
  float clipZ = globeComputeClippingZ(sp) * r.w;
  r.z = mix(0.0, clipZ, clamp((u_projection_transition - 0.2) / 0.8, 0.0, 1.0));
  return r;
  #else
  return projectTileFor3D(p, mercZ(e, p.y));
  #endif
}
// a segment end behind the camera (w <= 0) flips on projection and the quad explodes; shorten it to the near plane
const float NEAR_W = 0.002;
vec4 clipNear(vec4 a, vec4 b) { if (a.w >= NEAR_W) return a; if (b.w < NEAR_W) return a; float t = (NEAR_W - a.w) / (b.w - a.w); return mix(a, b, t); }
vec3 rainbow(float t) {
  // launch is cold, impact is hot (Cam, 2026-09-03): purple → blue → green → yellow → orange → red
  vec3 c0 = vec3(0.84, 0.0, 0.98), c1 = vec3(0.16, 0.47, 1.0), c2 = vec3(0.0, 0.90, 0.46), c3 = vec3(1.0, 0.92, 0.0), c4 = vec3(1.0, 0.57, 0.0), c5 = vec3(1.0, 0.09, 0.27);
  float u = clamp(t, 0.0, 1.0) * 5.0; float k = floor(u); float f = u - k;
  if (k < 1.0) return mix(c0, c1, f); if (k < 2.0) return mix(c1, c2, f); if (k < 3.0) return mix(c2, c3, f); if (k < 4.0) return mix(c3, c4, f); return mix(c4, c5, f);
}`;

const LINE_VS = `
${COMMON}
uniform float u_rainbow;
in vec2 a_pos; in float a_elev; in vec2 a_pos2; in float a_elev2; in float a_side;
in vec3 a_meta;      // t along the arc, kind (0 arc, 1 chase, 2 drop), mine (0/1)
in float a_progress; // this missile's progress (dynamic)
out vec4 v_color;
void main() {
  vec4 r0 = proj(a_pos, a_elev); vec4 r1 = proj(a_pos2, a_elev2);
  if (r0.w < NEAR_W && r1.w < NEAR_W) { gl_Position = vec4(0.0, 0.0, 2.0, 1.0); v_color = vec4(0.0); return; } // wholly behind the eye
  vec4 c0 = clipNear(r0, r1); vec4 c1 = clipNear(r1, r0);
  vec2 s0 = c0.xy / c0.w * u_viewport; vec2 s1 = c1.xy / c1.w * u_viewport;
  vec2 dir = s1 - s0; float len = length(dir); dir = len > 1e-4 ? dir / len : vec2(1.0, 0.0);
  vec2 nrm = vec2(-dir.y, dir.x);
  float kind = a_meta.y, t = a_meta.x, mine = a_meta.z;
  bool done = t <= a_progress;
  float width = kind == 0.0 ? (done ? 3.0 : 1.5) : kind == 1.0 ? 2.0 : 1.0;
  vec3 rgb; float alpha;
  if (kind == 0.0) {
    if (done) { rgb = u_rainbow > 0.5 ? rainbow(t) : (mine > 0.5 ? vec3(0.25, 0.83, 0.48) : vec3(0.72, 0.11, 0.11)); alpha = 0.95; }
    else { rgb = mine > 0.5 ? vec3(0.25, 0.83, 0.48) : vec3(1.0, 0.35, 0.21); alpha = 0.3; }
  } else if (kind == 1.0) { rgb = vec3(0.55, 0.71, 0.0); alpha = 0.92; }
  else { rgb = vec3(1.0); alpha = 0.3; }
  v_color = vec4(rgb * alpha, alpha); // premultiplied
  vec2 off = nrm * a_side * width * 0.5 / u_viewport; // half-width in clip units (u_viewport is half the size in px)
  gl_Position = vec4(c0.xy + off * c0.w, c0.z, c0.w);
}`;

const LINE_FS = `precision mediump float; in vec4 v_color; out vec4 fragColor; void main() { fragColor = v_color; }`;

const SPRITE_VS = `
${COMMON}
uniform float u_size;
in vec2 a_pos; in float a_elev; in vec2 a_pos2; in float a_elev2; // where it is, and a point just ahead (for the heading)
in vec2 a_corner; in vec2 a_uv;
out vec2 v_uv;
void main() {
  vec4 c0 = proj(a_pos, a_elev); vec4 c1 = proj(a_pos2, a_elev2);
  if (c0.w < NEAR_W) { gl_Position = vec4(0.0, 0.0, 2.0, 1.0); v_uv = vec2(0.0); return; } // behind the eye
  c1 = clipNear(c1, c0);
  vec2 s0 = c0.xy / c0.w * u_viewport; vec2 s1 = c1.xy / c1.w * u_viewport;
  vec2 dir = s1 - s0; float len = length(dir); dir = len > 1e-4 ? dir / len : vec2(0.0, 1.0);
  // the art points up; rotate "up" onto the heading
  vec2 up = dir, right = vec2(dir.y, -dir.x);
  vec2 r = right * a_corner.x + up * a_corner.y;
  vec2 off = r * u_size * 0.5 / u_viewport;
  gl_Position = vec4(c0.xy + off * c0.w, c0.z, c0.w);
  v_uv = a_uv;
}`;

const SPRITE_FS = `precision mediump float; uniform sampler2D u_tex; in vec2 v_uv; out vec4 fragColor; void main() { vec4 c = texture(u_tex, v_uv); fragColor = vec4(c.rgb * c.a, c.a); }`;

// ---- geometry cache -------------------------------------------------------------------------------

interface ArcGeom { key: string; n: number; xy: Float32Array /* (n+1)×2 mercator */; alt: Float32Array; totalKm: number; launch: Pos; brg: number; peak: number; x0: number; y0: number; x1: number; y1: number }
const overlaps = (g: { x0: number; y0: number; x1: number; y1: number }, b: { x0: number; y0: number; x1: number; y1: number }) => g.x1 >= b.x0 && g.x0 <= b.x1 && g.y1 >= b.y0 && g.y0 <= b.y1;
/** One line segment as a screen-extruded quad: A(+), A(−), B(+), B(−); B vertices point back at A with the side flipped so the normal agrees. */
function pushSeg(d: Float32Array, idx: Uint32Array, v: number, s: number, ax: number, ay: number, ae: number, bx: number, by: number, be: number, t: number, kind: number, mine: number): number {
  const F = 10;
  const rows: [number, number, number, number, number, number, number][] = [[ax, ay, ae, bx, by, be, 1], [ax, ay, ae, bx, by, be, -1], [bx, by, be, ax, ay, ae, -1], [bx, by, be, ax, ay, ae, 1]];
  for (const r of rows) { const o = v * F; d[o] = r[0]; d[o + 1] = r[1]; d[o + 2] = r[2]; d[o + 3] = r[3]; d[o + 4] = r[4]; d[o + 5] = r[5]; d[o + 6] = r[6]; d[o + 7] = t; d[o + 8] = kind; d[o + 9] = mine; v++; }
  const b0 = v - 4; idx.set([b0, b0 + 1, b0 + 2, b0 + 2, b0 + 1, b0 + 3], s * 6);
  return v;
}

const SPRITES = ["marker_missile", "marker_missilenuke", "marker_ecm"];
const SPRITE_CORNERS: [number, number, number, number][] = [[-1, 1, 0, 0], [1, 1, 1, 0], [-1, -1, 0, 1], [-1, -1, 0, 1], [1, 1, 1, 0], [1, -1, 1, 1]];
const CELL = 128;

export class ArcsLayer implements CustomLayerInterface {
  id = "arcs-gl";
  type = "custom" as const;
  renderingMode = "3d" as const;
  private map!: MLMap;
  private gl: WebGL2RenderingContext | null = null;
  private programs = new Map<string, { line: WebGLProgram; sprite: WebGLProgram }>();
  private vaoLine: WebGLVertexArrayObject | null = null;
  private vaoSprite: WebGLVertexArrayObject | null = null;
  private bufStatic: WebGLBuffer | null = null;   // line vertices, 10 floats each
  private bufProgress: WebGLBuffer | null = null; // 1 float per line vertex, per frame
  private bufIndex: WebGLBuffer | null = null;
  private bufSprite: WebGLBuffer | null = null;   // 10 floats per vertex, per frame
  private bufChase: WebGLBuffer | null = null; private bufChaseIdx: WebGLBuffer | null = null; private vaoChase: WebGLVertexArrayObject | null = null;
  private tex: WebGLTexture | null = null;
  private texReady = false;
  private geom = new Map<number, ArcGeom>();
  private segOf = new Map<number, [number, number]>(); // missile id → [first vertex, vertex count]
  private lineVerts = 0;
  private staticDirty = true;
  private staticData = new Float32Array(0);
  private indexData = new Uint32Array(0);
  private progressData = new Float32Array(0);
  private spriteData = new Float32Array(0);
  spriteCount = 0;
  /** Set by the map when the 3-D layer draws rockets: poses are still computed (the models use them), sprites are not. */
  spritesOff = false;
  /** This frame's rockets in the air (altitude mode only): mercator x/y + altitude, and the next arc sample for the heading. */
  poses: { id: number; x: number; y: number; alt: number; x1: number; y1: number; alt1: number; tex: number }[] = [];
  private rainbow = true;
  arcsDrawn = 0;
  readonly off = !arcsEnabled();
  /** MapLibre's projection matrix from the last render — lets us put a lifted point on screen exactly where the GPU does. */
  mainMatrix: Float64Array | null = null; // read by the models layer too (depth compensation for lifted rockets)
  /** Screen position (CSS px) of a rocket at its current altitude, or null if unknown / behind the camera. */
  liftedScreen(mis: any, now: number): { x: number; y: number } | null {
    const M = this.mainMatrix; if (!M || !this.map) return null;
    const g = this.geom.get(mis.id); if (!g) return null;
    const target: Pos | null = mis.tracking ? (world.players.get(mis.targetId)?.pos ?? mis.target) : mis.target;
    if (!target) return null;
    const fl = this.flightOf(mis, target); if (!fl) return null;
    const remainingKm = Math.max(0, fl.ahead - fl.speed * (now - fl.seenAt));
    const progress = Math.min(1, Math.max(0, 1 - remainingKm / g.totalKm));
    const p = move(g.launch, g.brg, g.totalKm * progress), alt = g.peak * Math.sin(Math.PI * progress);
    const m = (this.map as any).transform.getMatrixForModel({ lng: p.lon, lat: p.lat }, alt) as ArrayLike<number>; // model → world
    // clip = M × m × origin — origin is column 3 of m
    const wx = m[12], wy = m[13], wz = m[14], ww = m[15];
    const cx = M[0] * wx + M[4] * wy + M[8] * wz + M[12] * ww, cy = M[1] * wx + M[5] * wy + M[9] * wz + M[13] * ww, cw = M[3] * wx + M[7] * wy + M[11] * wz + M[15] * ww;
    if (!(cw > 1e-6)) return null;
    const c = this.map.getContainer();
    return { x: (cx / cw * 0.5 + 0.5) * c.clientWidth, y: (0.5 - cy / cw * 0.5) * c.clientHeight };
  }
  variant = "";

  onAdd(map: MLMap, gl: WebGL2RenderingContext) {
    this.map = map; this.gl = gl;
    this.bufStatic = gl.createBuffer(); this.bufProgress = gl.createBuffer(); this.bufIndex = gl.createBuffer(); this.bufSprite = gl.createBuffer();
    this.bufChase = gl.createBuffer(); this.bufChaseIdx = gl.createBuffer();
    this.vaoLine = gl.createVertexArray(); this.vaoSprite = gl.createVertexArray(); this.vaoChase = gl.createVertexArray();
    this.tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, this.tex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array([0, 0, 0, 0]));
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    // atlas: the three rocket sprites side by side
    const canvas = document.createElement("canvas"); canvas.width = CELL * SPRITES.length; canvas.height = CELL;
    const ctx = canvas.getContext("2d")!;
    let loaded = 0;
    SPRITES.forEach((name, i) => {
      const im = new Image(); im.src = `/art/${name}.png`;
      im.onload = () => {
        ctx.drawImage(im, i * CELL, 0, CELL, CELL);
        if (++loaded === SPRITES.length && this.gl) {
          const g = this.gl; g.bindTexture(g.TEXTURE_2D, this.tex); g.pixelStorei(g.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false); g.pixelStorei(g.UNPACK_FLIP_Y_WEBGL, false);
          g.texImage2D(g.TEXTURE_2D, 0, g.RGBA, g.RGBA, g.UNSIGNED_BYTE, canvas); this.texReady = true; this.map.triggerRepaint();
        }
      };
    });
  }
  onRemove() {
    const gl = this.gl; if (!gl) return;
    for (const p of this.programs.values()) { gl.deleteProgram(p.line); gl.deleteProgram(p.sprite); }
    this.programs.clear();
    for (const b of [this.bufStatic, this.bufProgress, this.bufIndex, this.bufSprite, this.bufChase, this.bufChaseIdx]) if (b) gl.deleteBuffer(b);
    if (this.tex) gl.deleteTexture(this.tex);
    for (const va of [this.vaoLine, this.vaoSprite, this.vaoChase]) if (va) gl.deleteVertexArray(va);
    this.gl = null; this.staticDirty = true; this.texReady = false;
  }

  private compile(gl: WebGL2RenderingContext, vs: string, fs: string): WebGLProgram {
    const sh = (type: number, src: string) => { const s = gl.createShader(type)!; gl.shaderSource(s, src); gl.compileShader(s); if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error("[arcs-gl] shader: " + gl.getShaderInfoLog(s) + "\n" + src.split("\n").slice(0, 3).join("\n")); return s; };
    const p = gl.createProgram()!;
    gl.attachShader(p, sh(gl.VERTEX_SHADER, vs)); gl.attachShader(p, sh(gl.FRAGMENT_SHADER, fs)); gl.linkProgram(p);
    if (!gl.getProgramParameter(p, gl.LINK_STATUS)) throw new Error("[arcs-gl] link: " + gl.getProgramInfoLog(p));
    return p;
  }
  private program(gl: WebGL2RenderingContext, sd: { variantName: string; vertexShaderPrelude: string; define: string }) {
    let p = this.programs.get(sd.variantName);
    if (!p) {
      const head = `#version 300 es\nprecision highp float;\n${sd.vertexShaderPrelude}\n${sd.define}\n`;
      p = { line: this.compile(gl, head + LINE_VS, "#version 300 es\n" + LINE_FS), sprite: this.compile(gl, head + SPRITE_VS, "#version 300 es\n" + SPRITE_FS) };
      this.programs.set(sd.variantName, p);
    }
    return p;
  }

  // ---- data ---------------------------------------------------------------------------------------

  private arcGeom(mis: any, target: Pos, zoomKey: number): ArcGeom | null {
    const launch: Pos = mis.launch ?? mis.pos;
    const key = `${zoomKey}|${target.lat.toFixed(4)}|${target.lon.toFixed(4)}|${launch.lat.toFixed(4)}|${launch.lon.toFixed(4)}`;
    const have = this.geom.get(mis.id);
    if (have && have.key === key) return have;
    const totalKm = distanceKm(launch, target);
    if (totalKm < 0.5) return null;
    const brg = bearing(launch, target), peak = peakM(totalKm, zoomKey, (launch.lat + target.lat) / 2);
    const n = Math.max(16, Math.min(96, Math.round(totalKm / 40)));
    const xy = new Float32Array((n + 1) * 2), alt = new Float32Array(n + 1);
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
    for (let i = 0; i <= n; i++) {
      const u = i / n, m = merc(move(launch, brg, totalKm * u));
      xy[i * 2] = m[0]; xy[i * 2 + 1] = m[1]; alt[i] = peak * Math.sin(Math.PI * u);
      if (m[0] < x0) x0 = m[0]; if (m[0] > x1) x1 = m[0]; if (m[1] < y0) y0 = m[1]; if (m[1] > y1) y1 = m[1];
    }
    const g = { key, n, xy, alt, totalKm, launch, brg, peak, x0, y0, x1, y1 };
    this.geom.set(mis.id, g);
    return g;
  }

  /** Static line vertices for the arcs in view: 4 per segment (both ends × both sides), 10 floats each. */
  private rebuildStatic(arcs: { mis: any; g: ArcGeom }[]) {
    let segs = 0;
    for (const { g } of arcs) segs += g.n;
    const F = 10, verts = segs * 4;
    const d = new Float32Array(verts * F), idx = new Uint32Array(segs * 6);
    let v = 0, sIdx = 0;
    const me = world.me;
    this.segOf.clear();
    for (const { mis, g } of arcs) {
      const first = v, mine = me && mis.ownerId === me.id ? 1 : 0;
      for (let i = 0; i < g.n; i++) {
        const ax = g.xy[i * 2], ay = g.xy[i * 2 + 1], ae = g.alt[i], bx = g.xy[(i + 1) * 2], by = g.xy[(i + 1) * 2 + 1], be = g.alt[i + 1], t = i / g.n;
        v = pushSeg(d, idx, v, sIdx++, ax, ay, ae, bx, by, be, t, 0, mine);
      }
      this.segOf.set(mis.id, [first, v - first]);
    }
    this.staticData = d; this.indexData = idx; this.lineVerts = verts;
    this.progressData = new Float32Array(verts);
    this.staticDirty = true;
  }

  /** The view as a mercator box with a margin; null = everything is in view (globe zooms). */
  private viewBox(): { x0: number; y0: number; x1: number; y1: number } | null {
    if (this.map.getZoom() < 4) return null;
    const b = this.map.getBounds();
    const sw = merc({ lat: b.getSouth(), lon: b.getWest() }), ne = merc({ lat: b.getNorth(), lon: b.getEast() });
    const mx = (ne[0] - sw[0]) * 0.5, my = (sw[1] - ne[1]) * 0.5; // mercator y grows southward
    return { x0: sw[0] - mx, x1: ne[0] + mx, y0: ne[1] - my, y1: sw[1] + my };
  }
  /** Per-missile flight numbers that only change when the server re-bases the missile (seenAt): after that, progress is arithmetic. */
  private flight = new Map<number, { seenAt: number; ahead: number; speed: number; type: any; target: Pos }>();
  private flightOf(mis: any, target: Pos) {
    const c = this.flight.get(mis.id);
    if (c && c.seenAt === mis.seenAt && c.target === target) return c;
    const type = missileType(mis.type); if (!type) return null;
    const speed = world.balance.weapons.indices.missileSpeedKph[type.speedIndex] / 3_600_000; // km per ms
    const f = { seenAt: mis.seenAt, ahead: distanceKm(mis.pos, target), speed, type, target };
    this.flight.set(mis.id, f);
    return f;
  }
  private lastViewKey = "";
  private lastVisibleIds = "";
  private chaseData = new Float32Array(0);
  private chaseIndex = new Uint32Array(0);

  /** Called every animation frame; returns the number of arcs in view. */
  update(now: number, filters: MapFilters, settings: MapSettings): number {
    if (this.off || settings.lowDetail || !world.balance || (!filters.missiles && !filters.interceptors)) { this.arcsDrawn = 0; this.spriteCount = 0; if (this.lineVerts) { this.lineVerts = 0; this.staticDirty = true; } this.chaseIndex = new Uint32Array(0); this.map?.triggerRepaint(); return 0; }
    this.rainbow = settings.trail === "rainbow";
    const zoomKey = Math.round(this.map.getZoom() * 4) / 4;
    const box = this.viewBox();
    const viewKey = box ? `${zoomKey}|${box.x0.toFixed(4)}|${box.y0.toFixed(4)}|${box.x1.toFixed(4)}|${box.y1.toFixed(4)}` : `${zoomKey}|all`;
    const arcs: { mis: any; g: ArcGeom; progress: number }[] = [];
    let geomChanged = false;
    const seen = new Set<number>();
    const sprites: number[] = [];
    const poses: typeof this.poses = [];
    const altitudeMode = filters.arcs !== false; // ground / none: no sprites in the air, rockets sit on the map at sea level (Cam, 2026-09-03)
    if (filters.missiles && altitudeMode) {
      for (const mis of world.missiles.values()) {
        if (!mis.pos) continue;
        seen.add(mis.id);
        // culling (Cam, 2026-09-03): an arc whose box misses the view costs nothing this frame
        const cached = this.geom.get(mis.id);
        if (cached && box && !overlaps(cached, box)) continue;
        const target: Pos | null = mis.tracking ? (world.players.get(mis.targetId)?.pos ?? mis.target) : mis.target;
        if (!target) continue;
        const g = this.arcGeom(mis, target, zoomKey);
        if (!g) continue;
        if (g !== cached) geomChanged = true;
        if (box && !overlaps(g, box)) continue;
        const fl = this.flightOf(mis, target); if (!fl) continue;
        const remainingKm = Math.max(0, fl.ahead - fl.speed * (now - fl.seenAt));
        const progress = Math.min(1, Math.max(0, 1 - remainingKm / g.totalKm));
        arcs.push({ mis, g, progress });
        // rocket position + heading by interpolating the arc's own samples — no trig per frame
        const u = progress * g.n, i = Math.min(g.n - 1, Math.floor(u)), fr = u - i;
        const x0 = g.xy[i * 2], y0 = g.xy[i * 2 + 1], x1 = g.xy[(i + 1) * 2], y1 = g.xy[(i + 1) * 2 + 1];
        const px = x0 + (x1 - x0) * fr, py = y0 + (y1 - y0) * fr, alt = g.alt[i] + (g.alt[i + 1] - g.alt[i]) * fr;
        const tex = fl.type.nuclear ? 1 : fl.type.ecm ? 2 : 0;
        const u0 = tex / SPRITES.length, u1 = (tex + 1) / SPRITES.length;
        poses.push({ id: mis.id, x: px, y: py, alt, x1, y1, alt1: g.alt[i + 1], tex });
        if (!this.spritesOff) for (const [cx, cy, uu, vv] of SPRITE_CORNERS) sprites.push(px, py, alt, x1, y1, g.alt[i + 1], cx, cy, uu === 0 ? u0 : u1, vv);
      }
    }
    for (const id of this.geom.keys()) if (!seen.has(id)) { this.geom.delete(id); this.flight.delete(id); geomChanged = true; }
    // the static buffer holds the arcs in view; rebuilt when the view, the zoom step or the set changes
    const visibleIds = arcs.length + ":" + (arcs.length ? arcs[0].mis.id + ":" + arcs[arcs.length - 1].mis.id : "");
    if (geomChanged || viewKey !== this.lastViewKey || visibleIds !== this.lastVisibleIds || (arcs.length > 0 && this.lineVerts === 0)) { this.rebuildStatic(arcs); this.lastViewKey = viewKey; this.lastVisibleIds = visibleIds; }
    if (filters.arcs !== false) for (const { mis, progress } of arcs) { const r = this.segOf.get(mis.id); if (r) this.progressData.fill(progress, r[0], r[0] + r[1]); }
    // chase lines move every frame: their own small buffer, never the static one
    let chaseSegs = 0;
    const chase: number[] = [];
    if (filters.interceptors && (filters.arcs !== false || filters.tracks !== false)) { // chase lines show in both line modes, not in "none"
      for (const i of world.interceptors.values()) {
        if (!i.pos) continue;
        const m = i.targetId !== undefined ? world.missiles.get(i.targetId) : null;
        const f = m ? missileFlight(m, now) : null;
        if (!f) continue;
        const a = merc(i.pos), bm = merc(f.pos);
        if (box && Math.max(a[0], bm[0]) < box.x0 || box && Math.min(a[0], bm[0]) > box.x1 || box && Math.max(a[1], bm[1]) < box.y0 || box && Math.min(a[1], bm[1]) > box.y1) continue;
        // along the great circle: a straight mercator chord over thousands of km bends into corners on the globe
        // (Cam's "oddball green lines", 2026-09-03). Short chases stay one segment; long ones sample every ~150 km.
        const km = distanceKm(i.pos, f.pos);
        const n = Math.max(1, Math.min(32, Math.round(km / 150)));
        const brg = n > 1 ? bearing(i.pos, f.pos) : 0;
        let prev = a;
        for (let k = 1; k <= n; k++) { const q = k === n ? bm : merc(move(i.pos, brg, (km * k) / n)); chase.push(prev[0], prev[1], q[0], q[1]); prev = q; chaseSegs++; }
      }
    }
    const cd = new Float32Array(chaseSegs * 40), ci = new Uint32Array(chaseSegs * 6);
    for (let k = 0, v = 0; k < chaseSegs; k++) v = pushSeg(cd, ci, v, k, chase[k * 4], chase[k * 4 + 1], 0, chase[k * 4 + 2], chase[k * 4 + 3], 0, 0, 1, 0);
    this.chaseData = cd; this.chaseIndex = ci;
    this.arcsDrawn = filters.arcs === false ? 0 : arcs.length;
    this.drawArcs = filters.arcs !== false;
    this.spriteData = Float32Array.from(sprites); this.spriteCount = sprites.length / 10; this.poses = poses;
    this.map.triggerRepaint();
    return this.arcsDrawn;
  }
  private drawArcs = true;

  // ---- render -------------------------------------------------------------------------------------

  render(gl: WebGL2RenderingContext, args: any) {
    if (this.off || !this.gl) return;
    const sd = args.shaderData, pd = args.defaultProjectionData;
    if (!sd || !pd) return;
    this.variant = sd.variantName;
    this.mainMatrix = Float64Array.from(pd.mainMatrix);
    let progs: { line: WebGLProgram; sprite: WebGLProgram };
    try { progs = this.program(gl, sd); } catch (e) { if (!this.warned) { this.warned = true; console.warn(String(e)); } return; }
    const setProjection = (p: WebGLProgram) => {
      const u = (n: string) => gl.getUniformLocation(p, n);
      const m = u("u_projection_matrix"); if (m) gl.uniformMatrix4fv(m, false, pd.mainMatrix);
      const fb = u("u_projection_fallback_matrix"); if (fb) gl.uniformMatrix4fv(fb, false, pd.fallbackMatrix);
      const tc = u("u_projection_tile_mercator_coords"); if (tc) gl.uniform4f(tc, pd.tileMercatorCoords[0], pd.tileMercatorCoords[1], pd.tileMercatorCoords[2], pd.tileMercatorCoords[3]);
      const cp = u("u_projection_clipping_plane"); if (cp) gl.uniform4f(cp, pd.clippingPlane[0], pd.clippingPlane[1], pd.clippingPlane[2], pd.clippingPlane[3]);
      const tr = u("u_projection_transition"); if (tr) gl.uniform1f(tr, pd.projectionTransition);
      const vp = u("u_viewport"); if (vp) gl.uniform2f(vp, gl.drawingBufferWidth / 2, gl.drawingBufferHeight / 2);
    };
    gl.disable(gl.DEPTH_TEST); gl.disable(gl.CULL_FACE);
    gl.enable(gl.BLEND); gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    // ---- lines
    const lineAttrs = (buf: WebGLBuffer | null) => {
      const F = 40; // bytes per vertex
      const attr = (name: string, size: number, offset: number) => { const loc = gl.getAttribLocation(progs.line, name); if (loc < 0) return; gl.enableVertexAttribArray(loc); gl.vertexAttribPointer(loc, size, gl.FLOAT, false, F, offset); };
      gl.bindBuffer(gl.ARRAY_BUFFER, buf);
      attr("a_pos", 2, 0); attr("a_elev", 1, 8); attr("a_pos2", 2, 12); attr("a_elev2", 1, 20); attr("a_side", 1, 24); attr("a_meta", 3, 28);
    };
    // ---- chase lines (own small buffer, rebuilt every frame)
    if (this.chaseIndex.length > 0) {
      gl.useProgram(progs.line); setProjection(progs.line);
      gl.uniform1f(gl.getUniformLocation(progs.line, "u_rainbow"), this.rainbow ? 1 : 0);
      gl.bindVertexArray(this.vaoChase);
      gl.bindBuffer(gl.ARRAY_BUFFER, this.bufChase); gl.bufferData(gl.ARRAY_BUFFER, this.chaseData, gl.DYNAMIC_DRAW);
      lineAttrs(this.bufChase);
      const pl = gl.getAttribLocation(progs.line, "a_progress"); if (pl >= 0) { gl.disableVertexAttribArray(pl); gl.vertexAttrib1f(pl, 0); }
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.bufChaseIdx); gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, this.chaseIndex, gl.DYNAMIC_DRAW);
      gl.drawElements(gl.TRIANGLES, this.chaseIndex.length, gl.UNSIGNED_INT, 0);
      gl.bindVertexArray(null);
    }
    if (this.lineVerts > 0 && this.drawArcs) {
      gl.useProgram(progs.line); setProjection(progs.line);
      gl.uniform1f(gl.getUniformLocation(progs.line, "u_rainbow"), this.rainbow ? 1 : 0);
      gl.bindVertexArray(this.vaoLine);
      gl.bindBuffer(gl.ARRAY_BUFFER, this.bufStatic);
      if (this.staticDirty) { gl.bufferData(gl.ARRAY_BUFFER, this.staticData, gl.STATIC_DRAW); gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.bufIndex); gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, this.indexData, gl.STATIC_DRAW); this.staticDirty = false; }
      else gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.bufIndex);
      lineAttrs(this.bufStatic);
      gl.bindBuffer(gl.ARRAY_BUFFER, this.bufProgress); gl.bufferData(gl.ARRAY_BUFFER, this.progressData, gl.DYNAMIC_DRAW);
      const pl = gl.getAttribLocation(progs.line, "a_progress"); if (pl >= 0) { gl.enableVertexAttribArray(pl); gl.vertexAttribPointer(pl, 1, gl.FLOAT, false, 4, 0); }
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.bufIndex);
      gl.drawElements(gl.TRIANGLES, this.indexData.length, gl.UNSIGNED_INT, 0);
      gl.bindVertexArray(null);
    }
    // ---- sprites
    if (this.spriteCount > 0 && this.texReady) {
      gl.useProgram(progs.sprite); setProjection(progs.sprite);
      // same zoom scaling as the map markers: 15% at world view → full size from zoom 12
      const z = this.map.getZoom(), zf = z <= 0 ? 0.15 : z <= 2 ? 0.15 + (z / 2) * 0.15 : z <= 6 ? 0.3 + ((z - 2) / 4) * 0.3 : z <= 10 ? 0.6 + ((z - 6) / 4) * 0.25 : z <= 12 ? 0.85 + ((z - 10) / 2) * 0.15 : 1;
      gl.uniform1f(gl.getUniformLocation(progs.sprite, "u_size"), 96 * zf * (window.devicePixelRatio || 1));
      gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, this.tex); gl.uniform1i(gl.getUniformLocation(progs.sprite, "u_tex"), 0);
      gl.bindVertexArray(this.vaoSprite);
      gl.bindBuffer(gl.ARRAY_BUFFER, this.bufSprite); gl.bufferData(gl.ARRAY_BUFFER, this.spriteData, gl.DYNAMIC_DRAW);
      const F = 40;
      const attr = (name: string, size: number, offset: number) => { const loc = gl.getAttribLocation(progs.sprite, name); if (loc < 0) return; gl.enableVertexAttribArray(loc); gl.vertexAttribPointer(loc, size, gl.FLOAT, false, F, offset); };
      attr("a_pos", 2, 0); attr("a_elev", 1, 8); attr("a_pos2", 2, 12); attr("a_elev2", 1, 20); attr("a_corner", 2, 24); attr("a_uv", 2, 32);
      gl.drawArrays(gl.TRIANGLES, 0, this.spriteCount);
      gl.bindVertexArray(null);
    }
  }
  private warned = false;
}
